<template>
  <div class="landing-page-v2 cyber-monday-lifetime-2024" :class="[$config.platform, { extend: promotion.extend }]">
    <Hero
      :stats="stats" :assets="assets.hero" :cols="{ left: 'col-5 offset-1', right: 'col-5' }"
      :benefits="benefits"
    >
      <template slot="title">
        <h1 class="main">
          <component :is="logo" />
          <span class="highlight white">
            {{ promotion.discount }}% OFF
          </span>
        </h1>
        <span class="block text-2">
          <strong class="highlight yellow">Top {{ $config.platform === 'ds' ? 'Data and AI' : 'Finance' }} Courses</strong>—Always Expanding, <strong>Forever Yours</strong>
        </span>
      </template>
      <div slot="content" class="card">
        <p class="label">
          Exclusive Offer
        </p>
        <p class="old-price">
          <span class="del">&nbsp;{{ page.prices.basePrice | formatCurrency(prices.currency, { minimumFractionDigits: 0 }) }}&nbsp;</span>
        </p>
        <p class="new-price">
          <span class="discounted-price">{{ priceSplitted[0] | formatCurrency(prices.currency, { minimumFractionDigits: 0 }) }}</span>
          <span v-if="priceSplitted[1]" class="medium">.{{ priceSplitted[1] }}</span>
        </p>
        <p class="text">
          Billed Once
        </p>
        <CounterWithDiscount
          v-if="countdown"
          :key="promotion.discount"
          :end-date="countdownEndDate"
          counter-class="filled"
        />
        <nuxt-link :to="`/checkout/?plan=lifetime&coupon=${promotion.coupon}`" class="btn-primary campaign-1 submit">
          Buy Now
        </nuxt-link>
        <p class="card-text">
          14-day money-back guarantee
        </p>
        <div v-if="assets.hero.stamp" class="stamp">
          <img :src="require(`../assets/images/campaigns/${$config.platform}-cml2024-hero-stamp.webp`)">
        </div>
      </div>
    </Hero>
    <section class="technologies dark">
      <p class="technologies-title">
        Learn with instructors from:
      </p>
      <div class="container">
        <div v-for="(tech) in companies" :key="tech.icon" class="single-technology">
          <component
            :is="iconLogo(tech.icon)"
            :class="tech.icon"
            :title="tech.text"
          />
          <span v-if="tech.text" class="text">
            {{ tech.text }}
          </span>
        </div>
      </div>
    </section>
    <div class="section courses-extended">
      <div class="head container">
        <h2 class="section-title">
          <span class="highlight magenta fa-blue">UPSKILL</span> in {{ $config.platform === 'ds' ? 'Data & AI' : 'Finance' }} for Life
        </h2>
        <h3 class="muted">
          What’s Your Next Career Goal?
        </h3>
        <ul>
          <li
            v-for="(g, idx) in cGroups" :key="idx" :class="{ active: idx === courseGroupIdx }"
            @click="switchCourseGroup(idx)"
          >
            {{ g.name }}
          </li>
        </ul>
        <p class="description">
          Become a certified
          <span class="highlight yellow thin">{{ cGroup.description.title }}<span class="mobile-only">,</span></span><span class="desktop-only">,</span> earning
          <span class="static-salary-container">
            <span class="animation-salary" :class="{ animated: salaryAnimation.animatedOnScroll }">
              <span
                class="holder animation" :class="{ active: salaryAnimation.activate }"
                :style="`--width: ${salaryAnimation.width || 188}px`"
                v-html="salary"
              />
            </span>
          </span>
        </p>
      </div>
      <Courses
        :key="`c-slider${courseGroupIdx}`" :courses="cGroupCourses" :offset="-2"
        heading="h3"
      />
      <div class="btn-holder">
        <button
          class="btn-primary btn-lg"
          :data-event="`${$route.path.replace(/\//g, '')}_v1_courses.click`"
          @click="subscribe"
        >
          Get {{ promotion.discount }}% Off
        </button>
      </div>
    </div>
    <section class="section promo-video">
      <Video :video-js-loaded="videoJsLoaded" :video-id="videoIds[$config.platform]">
        <div slot="title" class="section-title">
          Learn. Grow. Repeat. <span class="highlight blue">FOREVER.</span>
        </div>
      </Video>
    </section>
    <section class="section free">
      <div class="container">
        <h2 class="section-title">
          What Will You <span class="highlight blue">GET</span>?
        </h2>
        <div v-if="$config.platform === 'ds'" class="section-description muted">
          With lifetime access to a growing library of online courses, practical exercises, exams, and real-world projects, you'll continuously build essential skills and earn certifications to stand out in the competitive job market. <strong class="highlight yellow">Gain immediate access</strong> to all new resources as they’re added. 365 Data Science empowers learners at every career stage with comprehensive, cutting-edge training programs—accessible anytime, forever.
        </div>
        <div v-else class="section-description muted">
          With lifetime access to a growing library of online courses, practical exercises, exams, and real-world projects, you'll continuously build essential skills and earn certifications to stand out in the competitive job market. <strong class="highlight yellow">Gain immediate access</strong> to all new resources as they’re added. 365 Financial Analyst empowers learners at every career stage with comprehensive, cutting-edge training programs—accessible anytime, forever.
        </div>
        <client-only>
          <div class="cards row">
            <div
              v-for="(card, idx) in benefitCards" :key="idx"
              slot="slide"
              class="card col col-4 sm-6"
              :class="`id-${idx}`"
            >
              <div class="image-wrapper">
                <img :src="require(`../assets/images/campaigns/${$config.platform}-${card.image}.webp`)" class="card-image">
              </div>
              <h4 class="card-title" v-html="card.title" />
              <span class="card-desc muted" v-html="card.text" />
            </div>
          </div>
        </client-only>
      </div>
      <div class="btn-holder">
        <button
          class="btn-primary btn-lg"
          :data-event="`${$route.path.replace(/\//g, '')}_v1_free.click`"
          @click="subscribe"
        >
          Get Lifetime Access
        </button>
      </div>
    </section>
    <div class="section instructors">
      <div class="instructors-slider">
        <div class="container heading">
          <h2 class="section-title" v-html="instructorsTitle" />
          <Companies
            v-if="$config.platform === 'fa' && companiesFa && companiesFa.length" :companies="companiesFa"
            no-fill title=""
          />
        </div>
        <SliderInfinite
          :key="`instr${$store.state.layout}`"
          :offset="0" heading="h2" heading-class="section-title"
        >
          <div
            v-for="(instructor, idx) in instructors"
            :key="`${idx}${$store.state.layout}`"
            slot="slide"
          >
            <component :is="$config.platform === 'ds' ? 'nuxt-link' : 'span'" :to="`/instructors/${instructor.slug}`">
              <img :src="require(`../assets/images/campaigns/${$config.platform}-cml2024-instructor-${instructor.image || instructor.slug}${$store.state.layout === 'mobile' ? '-mobile' : ''}.webp`)" :alt="instructor.name">
            </component>
          </div>
        </SliderInfinite>
      </div>
    </div>
    <HomeSectionInfinite
      v-if="projects && projects.length"
      classes="section projects dark campaign"
      title-class="title-highlight"
      :pre-title="projectsPreTitle"
      :subtitle="projectsDescription"
      :sections="projects"
      section-slug="projects"
      section-button-title="Explore Projects Library"
      hidden-button
      target-blank
      is-campaign
    >
      <template slot="pre-title">
        Need More Training?
      </template>
      <template v-if="$config.platform === 'ds'" slot="title">
        Build Your Portfolio with <strong class="highlight blue block">HANDS-ON PROJECTS</strong>
      </template>
      <template v-else slot="title">
        Gain Job-Equivalent Experience with <strong class="highlight blue block">HANDS-ON PROJECTS</strong>
      </template>
      <button
        slot="button"
        class="btn-primary campaign-2" @click="subscribe"
      >
        Start Today
      </button>
    </HomeSectionInfinite>
    <template v-if="$config.platform === 'ds'">
      <div class="section graduations">
        <Videos v-if="$config.platform === 'ds'" :videos="videos" :video-js-loaded="videoJsLoaded">
          <template slot="title">
            <span class="section-title">
              Learn Together, <strong class="highlight blue">GROW FOREVER.</strong>
            </span>
          </template>
          <template slot="subtitle">
            <span class="h3">
              Join a Community of {{ $c.text('students.count') }}+ {{ $config.platform === 'fa' ? 'Finance Learners' : 'Learners' }}
            </span>
          </template>
        </Videos>
        <div class="container section-footer">
          <div class="row">
            <Companies
              v-if="companiesSmall && companiesSmall.length"
              :companies="companiesSmall" is-campaign
              title="Trusted by employees from:" no-fill
            />
          </div>
          <div class="trustpilot-review">
            <IconTrustpilot />
            <span class="rating">4.9/5</span>
            <StarsRatingCampaign />
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <section class="section long-reviews-holder">
        <Reviews
          :title="reviewsTitle" :subtitle="`Join a Community of ${$c.text('students.count')}+ Learners`" title-class="title-reset title-highlight"
          class="long-reviews" dark unique-class="second"
        />
      </section>
    </template>
    <section class="section limited-offer-holder">
      <LimitedOffer
        :options="limitedOfferOptions"
        :features="footerFeatures"
        campaign-page class-name="col-8"
        @subscribe="$router.push(`/checkout/?plan=lifetime&coupon=${promotion.coupon}`)"
      >
        <template slot="pre-title">
          <span>Exclusive Offer</span>
        </template>
      </LimitedOffer>
    </section>
    <client-only>
      <BottomBanner
        v-if="$store.state.layout === 'desktop'"
        :key="1 + countdownEndDate.getTime()"
        :options="bottomBannerOptions"
        :discount="promotion.discount"
        :closed="bannerClosed"
        :style="{ 'background-image': `url(${require(`../assets/images/campaigns/${$config.platform}-cml2024-bg-bottom-banner.webp`)})` }"
        @subscribe="subscribe"
        @close="closeBanner"
      >
        <template slot="heading">
          <div>
            <span class="highlight white">LIFETIME PASS</span>
            <span class="block">
              All {{ $config.platform === 'ds' ? 'Data and AI' : 'Finance' }} Courses
            </span>
          </div>
          <component :is="discountLogo" />
        </template>
        <CounterWithDiscount
          slot="countdown"
          :end-date="countdownEndDate" banner
          discount-text="Current Discount"
          counter-class="filled"
        />
      </BottomBanner>
    </client-only>
  </div>
</template>

<script>
import { CAMPAIGN_PROMOTIONS, CAMPAIGN_INSTRUCTORS } from '@itbpbg/lms-components/src/utils/campaigns';
import { getPromotion, dateInRightInterval, loadHotJar } from '@itbpbg/lms-components/src/utils/helper';
import global from '@itbpbg/lms-components/src/mixins/global';
import Companies from '@itbpbg/lms-components/src/Campaigns/Companies';
import SliderInfinite from '@itbpbg/lms-components/src/components/SliderInfinite';
import Videos from '@itbpbg/lms-components/src/Campaigns/Videos';
import CounterWithDiscount from '@itbpbg/lms-components/src/CounterWithDiscount';
import StarsRatingCampaign from '@itbpbg/lms-components/src/assets/svg/campaign-stars-rating.svg?vue-component';
import DSCMLifetimePlan from '@itbpbg/lms-components/src/assets/svg/ds-cm-lifetime-plan.svg?vue-component';
import DSCMLifetimeDiscount from '@itbpbg/lms-components/src/assets/svg/ds-cm-lifetime-discount.svg?vue-component';
import DSCMLifetimePlanMobile from '@itbpbg/lms-components/src/assets/svg/ds-cm-lifetime-plan-mobile.svg?vue-component';
import DSCMLifetimeDiscountMobile from '@itbpbg/lms-components/src/assets/svg/ds-cm-lifetime-discount-mobile.svg?vue-component';
import FACMLifetimePlan from '@itbpbg/lms-components/src/assets/svg/fa-cm-lifetime-plan.svg?vue-component';
import FACMLifetimeDiscount from '@itbpbg/lms-components/src/assets/svg/fa-cm-lifetime-discount.svg?vue-component';
import FACMLifetimePlanMobile from '@itbpbg/lms-components/src/assets/svg/fa-cm-lifetime-plan-mobile.svg?vue-component';
import FACMLifetimeDiscountMobile from '@itbpbg/lms-components/src/assets/svg/fa-cm-lifetime-discount-mobile.svg?vue-component';
import Hero from '../components/Campaigns/Hero';
import Courses from '../components/Campaigns/Courses';
import Video from '../components/Campaigns/Video';
import HomeSectionInfinite from '../components/Home/HomeSectionInfinite';
import Reviews from '../components/Landing/Reviews';
import LimitedOffer from '../components/Landing/LimitedOffer';
import BottomBanner from '../components/Landing/BottomBanner';
import IconTrustpilot from '../assets/svg/icon-trustpilot.svg?vue-component';

export default {
  components: {
    Hero,
    Companies,
    SliderInfinite,
    Courses,
    Video,
    HomeSectionInfinite,
    IconTrustpilot,
    StarsRatingCampaign,
    Videos,
    Reviews,
    CounterWithDiscount,
    LimitedOffer,
    BottomBanner,
    DSCMLifetimePlan,
    DSCMLifetimeDiscount,
    DSCMLifetimePlanMobile,
    DSCMLifetimeDiscountMobile,
    FACMLifetimePlan,
    FACMLifetimeDiscount,
    FACMLifetimePlanMobile,
    FACMLifetimeDiscountMobile,
  },
  mixins: [global],
  async asyncData({
    $axios,
    $config,
    route,
    error,
  }) {
    const {
      page, courses, stats, projects, coursesGroups,
    } = await $axios.$get('/pages/campaign/cyber-monday-lifetime-2024?prices=1&courses=1&coursesExtended=1&projects=1&projectsExtended=1')
      .catch(() => error({ statusCode: 404, message: 'Page not found' }));

    page.fullPath = `${$config.siteUrl}${route.fullPath}`;
    page.created = page.created || new Date();
    page.updated = page.updated || new Date();

    return {
      courses, stats, page, projects, coursesGroups,
    };
  },
  data() {
    return {
      assets: {
        hero: {
          stamp: true,
          background: `${this.$config.platform}-cml2024-bg-hero.webp`,
          backgroundMobile: `${this.$config.platform}-cml2024-bg-hero-mobile.webp`,
        },
        footer: {
          background: `${this.$config.platform}-cml2024-bg-footer.webp`,
          backgroundMobile: `${this.$config.platform}-cml2024-bg-footer-mobile.webp`,
        },
        backgroundsAsImages: false,
      },
      videoIds: {
        ds: '6365292950112',
        fa: '6365293664112',
      },
      videoJsLoaded: false,
      videoAlreadyPlayed: false,
      videoTimeout: null,
      bannerClosed: false,
      countdownEndDate: this.saleEndDate(),
      promotion: {},
      countdown: {
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00',
      },
      instructorsTitle: (this.$config.platform === 'ds'
        ? '<strong class="highlight blue">LIFETIME ACCESS</strong> to Expert-Led Data and AI Training'
        : '<strong class="highlight blue">LIFETIME ACCESS</strong> to Expert-Led Finance Training'),
      benefitCards: [
        {
          title: this.$config.platform === 'ds' ? 'Certificates' : 'Finance Certificates',
          text: (this.$config.platform === 'ds' ? 'Demonstrate your data and AI expertise by obtaining certifications that validate your skills.' : 'Demonstrate your finance expertise by earning finance certificates as a <strong>tangible proof</strong> of your skills.'),
          image: 'cml2024-certificates',
        },
        {
          title: 'Personalized Learning Plan',
          text: 'Build the ideal learning journey for your desired job and <strong>strengthen your abilities</strong> with a personalized skill plan.',
          image: 'cml2024-clp',
        },
        {
          title: 'Career-Focused Training',
          text: (this.$config.platform === 'ds' ? 'Embark on the data and <strong>AI career path of your dreams</strong> with a collection of courses to obtain role-specific skills.' : 'Embark on the finance <strong>career path of your dreams</strong> with a collection of courses to obtain role-specific skills.'),
          image: 'cml2024-tracks',
        },
        {
          title: 'Practice Tests',
          text: (this.$config.platform === 'ds' ? 'Test your newfound data and AI skills with our practical exams. Each <strong>course and career-focused training</strong> offers a verifiable certificate.' : 'Test your newfound finance skills with our practical exams. Each course and <strong>career-focused training</strong> offers a verifiable certificate.'),
          image: 'cml2024-exams',
        },
        {
          title: this.$config.platform === 'ds' ? 'Data Projects' : 'Finance Projects',
          text: (this.$config.platform === 'ds' ? 'Boost your portfolio with <strong>real-world data and ML projects</strong>. Learn by doing with business cases you\'ll encounter on the job.' : 'Boost your resume with <strong>real-world finance projects</strong> based on actionable use cases that provide valuable business insights.'),
          image: 'cml2024-projects',
        },
        {
          title: 'Interactive Exercises',
          text: (this.$config.platform === 'ds' ? 'Apply your knowledge with coding tasks, fill-in-the-blank exercises, true or false questions, <strong>flashcards</strong>, and more.' : 'Apply your knowledge with fill-in-the-blank exercises, true or false questions, <strong>flashcards</strong>, and more.'),
          image: 'cml2024-exercises',
        },
      ],
      videos: [
        {
          name: 'Lee McFarling',
          image: 'lee',
          from: 'USA',
          id: 6315230915112,
          quote: 'After 6 months of learning with 365 Data Science, I was getting job interviews as a business analyst or a data analyst.',
          job: 'Data Analyst, Astreya',
        },
        {
          name: 'Meghan Verschoor',
          image: 'meghan',
          from: 'Canada',
          id: 6315228912112,
          quote: 'I find the platform really easy to follow through with and learn the skills that I needed to apply to my job. I highly recommend it.',
          job: 'Director, Data Science & Management, Turnstone Biologics',
        },
        {
          name: 'Nada Abdulwahab',
          image: 'nada',
          from: 'Egypt',
          id: 6315228795112,
          quote: '365 gave me the basics to start my career […] and still helps me enhance my skills and push my career to a higher level.',
          job: 'Senior Data Engineer, VOIS',
        },
        {
          name: 'Julián Elizondo',
          image: 'julián',
          from: 'Mexico',
          id: 6343656185112,
          quote: 'If it wasn’t for 365 Data Science and the confidence it gave me, I wouldn’t have landed my current job.',
          job: 'Tableau Developer, Schneider Electric',
        },
        {
          name: 'Mihaela Paparinova',
          image: 'mihaela',
          from: 'Bulgaria',
          id: 6341101204112,
          quote: 'The program helped me excel in my studies and deepen my understanding of niche data science concepts.',
          job: 'Data Science Student, Sofia University',
        },
        {
          name: 'Ernest Asena',
          image: 'ernest',
          from: 'Kenya',
          id: 6315231920112,
          quote: 'Eight months into the program, I started applying for entry-level positions, and I landed a job as a junior data scientist.”',
          job: 'Data Insights Analytics Cloud Engineer, AWS',
        },
        {
          name: 'Ahmed Eldessouky',
          image: 'ahmed',
          from: 'Egypt',
          id: 6327293863112,
          quote: 'I was impressed with the quality of the video lessons, and the topics and different tools the program covers.',
          job: 'Business Developer, Squadio',
        },
      ],
      heroAnimation: {
        strings: [
          'data scientist',
          'data analyst',
          'business analyst',
          'ML engineer',
          'AI engineer',
        ],
        index: 0,
        animate: false,
      },
      salaryAnimation: {
        activate: false,
        animatedOnScroll: false,
      },
      technologies: [
        { icon: 'python', text: 'Python' },
        { icon: 'git', text: 'Git and Github' },
        { icon: 'tableau', text: 'Tableau' },
        { icon: 'r' },
        { icon: 'excel', text: 'Excel' },
        { icon: 'jupyter', text: 'Jupyter' },
        { icon: 'sql' },
        { icon: 'chatgpt', text: 'ChatGPT' },
        { icon: 'powerbi', text: 'Power BI' },
      ],
      companies: [
        ...(this.$config.platform === 'ds' ? [
          { icon: 'meta-new' },
          { icon: 'netflix' },
          { icon: 'google-new' },
          { icon: 'spotify' },
          { icon: 'ikea' },
          { icon: 'coca-cola-auto' },
          { icon: 'bbc' },
          { icon: 'pwc-new' },
        ] : [
          { icon: 'hsbc-new' },
          { icon: 'morgan-stanley-new' },
          { icon: 'goldman-sachs-new' },
          { icon: 'pwc-new' },
          { icon: 'coca-cola-auto' },
          { icon: 'ikea-new' },
        ]),
      ],
      companiesFa: [
        { icon: 'hsbc-new' },
        { icon: 'morgan-stanley-new' },
        { icon: 'goldman-sachs-new' },
        { icon: 'pwc-new' },
        { icon: 'coca-cola-auto' },
        { icon: 'ikea-new' },
      ],
      companiesSmall: [
        { icon: 'oracle' },
        { icon: 'amazon' },
        { icon: 'walmart' },
        { icon: 'alphabet' },
        { icon: 'meta' },
      ],
      instructors: CAMPAIGN_INSTRUCTORS[this.$config.platform],
      courseGroupIdx: 0,
      reviewsTitle: 'Learn Together, <strong class="highlight blue">GROW FOREVER.</strong>',
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    // const brightcoveSrc = [
    //   { src: 'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js' },
    //   { src: 'https://players.brightcove.net/videojs-quality-menu/1/videojs-quality-menu.min.js' },
    // ];

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps({ ...this.page, metaImage: this.page.ogImage || `og-campaign-${this.$config.platform}-cyber-monday-lifetime-2024.webp` }),

      script: [
        // ...brightcoveSrc,
        linkedData,
        {
          src:
            'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js',
          async: true,
        },
      ],
    };
  },
  computed: {
    logo() {
      const prefix = this.$config.platform.toUpperCase();
      if (this.$store.state.layout === 'mobile') {
        return `${prefix}CMLifetimePlanMobile`;
      }

      return `${prefix}CMLifetimePlan`;
    },
    discountLogo() {
      const prefix = this.$config.platform.toUpperCase();
      if (this.$store.state.layout === 'mobile') {
        return `${prefix}CMLifetimeDiscountMobile`;
      }

      return `${prefix}CMLifetimeDiscount`;
    },
    projectsPreTitle() {
      if (this.$config.platform === 'fa') {
        return 'Need More Training?';
      }

      return '';
    },
    projectsDescription() {
      if (this.$config.platform === 'fa') {
        return 'Bridge the gap between theory and practice with lifetime access to our constantly expanding finance projects. With beginner-friendly options, you can build valuable experience for your portfolio or tackle advanced topics anytime, at your own pace. Our curriculum is designed for learners to start upskilling <strong class="highlight yellow">today</strong>—preparing them to advance their finance careers <strong class="highlight yellow">tomorrow</strong>.';
      }

      return 'Bridge the gap between theory and practice with lifetime access to our ever-expanding collection of data and AI projects. Put your skills to practice from day one of your learning journey, with projects ranging from beginner to advanced levels. Then, build a comprehensive portfolio spanning diverse topics—from Excel to ML—showing employers you can tackle real-world data problems. We\'ve designed our curriculum so that learners can start upskilling <strong class="highlight yellow">today</strong>, ensuring they\'re prepared to grow their careers <strong class="highlight yellow">tomorrow</strong>.';
    },
    cGroups() {
      if (this.$config.platform === 'fa') {
        return this.coursesGroups;
      }

      const mapped = [
        'AI Engineer',
        'Machine Learning Engineer',
        'Data Analyst',
        'Data Scientist',
        'Business Intelligence Analyst',
        'Data Engineer',
        'Tableau Developer',
        'Power BI Developer',
        'Business Analyst',
      ];

      return mapped.map((t) => this.coursesGroups.find((g) => g.name === t) || {});
    },
    cGroup() {
      return this.cGroups[this.courseGroupIdx] || {};
    },
    cGroupCourses() {
      return this.cGroup.courses.map((id) => {
        if (typeof id === 'object') {
          return id;
        }

        return this.courses.find((c) => c.id === id) || {};
      }).filter((v) => Object.keys(v).length);
    },
    benefits() {
      if (this.$config.platform === 'fa') {
        return [
          '<strong>Pay once—</strong>access finance upskilling anytime',
          '<strong>Courses</strong> in Excel, financial analysis, modeling, accounting, corporate finance, investment banking, and more',
          '<strong>Certificates</strong> endorsed by industry experts',
          '<strong>Projects</strong> that mirror real business scenarios',
          '<strong>Structured career tracks</strong> to get you job-ready',
        ];
      }

      return [
        '<strong>Pay once—</strong>upskill in data and AI anytime',
        '<strong>Courses</strong> on Excel, data analysis and visualization, Python, R, SQL, ML, AI, and more',
        '<strong>Certificates</strong> to verify your skills to employers',
        '<strong>Projects</strong> using real data to boost your portfolio',
        '<strong>Structured career tracks</strong> to get you job-ready',
      ];
    },
    salary() {
      const [salary, year] = this.cGroup.description.earn.split('/');
      const salaryBolded = `<span class="letter bold">${salary.split('').join('</span><span class="letter bold">')}</span>`;
      const yearNormal = `<span class="letter">${year.split('').join('</span><span class="letter">')}</span>`;

      return `${salaryBolded}<span class="letter">/</span>${yearNormal}`;
    },
    limitedOfferOptions() {
      const price = this.page && this.page.prices.price ? this.page.prices.price : 119;
      // const baseAnnualPrice = this.page && this.page.prices.basePriceComparison ? this.page.prices.basePriceComparison : 432;

      return {
        preTitleSlot: true,
        htmltitle: `<strong class="highlight blue block">LIFETIME PASS</strong> Pay Once, Upskill Forever – Top ${this.$config.platform === 'ds' ? 'Data and AI' : 'Finance'} Courses at <span class="highlight yellow fa-campaign-3">${this.promotion.discount}% OFF</span>`,
        promoPlanType: 'lifetime',
        // comparisonPlanType: 'monthly',
        // comparisonPlanBilledText: 'Billed Monthly',
        // promoPlanBilledText: 'Billed Annually',
        regularPrice: this.$options.filters.formatCurrency(this.page.prices.basePrice, this.prices.currency, { minimumFractionDigits: 0 }),
        discountedPrice: this.$options.filters.formatCurrency(price, this.prices.currency, { minimumFractionDigits: 0 }),
        // billedComparisonPrice: this.$options.filters.formatCurrency(baseAnnualPrice, this.prices.currency, { minimumFractionDigits: 0 }),
        // discountedPriceCharge: `/y${this.$store.state.layout === 'mobile' ? '' : 'ear'}`,
        // billedComparisonCharge: '/month',
        promoPlanSubscribeClass: 'subscribe-annual',
        // comparisonSubscribeClass: 'subscribe-monthly',
        promoPlanButtonText: `Get ${this.promotion.discount}% Off`,
        buttonClass: '',
        // comparisonPlanButtonText: 'Sign up Now',
        vat: 'VAT may apply',
        badges: false,
        // badgesNeutral: true,
      };
    },
    bottomBannerOptions() {
      return {
        counterWithDiscount: true,
        subscribeClass: 'campaign-1',
        buttonText: 'Buy Now',
        className: this.promotion.final ? 'final-promo' : '',
        futureProof: true,
      };
    },
    footerFeatures() {
      const data = [
        { name: 'Pay once, access forever' },
        { name: `Real-world ${this.$config.platform === 'ds' ? 'data' : 'finance'} projects` },
        { name: 'Expert-led courses' },
        { name: 'Certificates of achievement' },
        { name: 'Self-paced online lessons' },
        { name: 'Resume builder' },
        { name: 'Career-focused training' },
        { name: 'Community forum' },
        { name: 'Interactive exercises' },
        { name: '14-day money-back guarantee' },
      ];

      if (this.$store.state.layout === 'mobile') {
        return [
          ...data.filter((v, i) => i % 2 === 0),
          ...data.filter((v, i) => i % 2 === 1),
        ];
      }

      return data;
    },
    prices() {
      return this.page.prices;
    },
    priceSplitted() {
      return (+this.prices.price || 0).toString().split('.');
    },
  },
  created() {
    this.countdownEndDate = this.saleEndDate();
  },
  async mounted() {
    loadHotJar();

    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJsLoaded = true;
    // Hero animation
    setInterval(() => {
      this.heroAnimation.animate = true;
      setTimeout(() => {
        this.heroAnimation.index = (this.heroAnimation.index + 1) % this.heroAnimation.strings.length;
      }, 300);

      setTimeout(() => {
        this.heroAnimation.animate = false;
      }, 600);
    }, 1200);

    this.$nextTick(() => this.toggleBanner());
    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJsLoaded = true;
    this.handleScroll();

    let stop = false;
    const interval = setInterval(() => {
      if (stop) {
        clearInterval(interval);
      }

      const nowDate = new Date();
      if (dateInRightInterval(nowDate)) {
        this.countdownEndDate = this.saleEndDate();
      }

      if (nowDate > new Date(this.promotion.endDateFinal)) {
        stop = true;
        window.location.href = `${this.$config.siteUrl}/offer-expired/`;
      }
    }, 1000);

    window.addEventListener('scroll', this.handleScroll);
    this.$once('hook:beforeDestroy', () => window.removeEventListener('scroll', this.handleScroll));
  },
  methods: {
    iconLogo(icon) {
      return icon ? require(`@itbpbg/lms-components/src/assets/svg/companies/logo-${icon}.svg?vue-component`) : '';
    },
    handleScroll() {
      if (process.client) {
        const salary = this.$el.querySelector('.animation-salary');
        if (salary && !this.salaryAnimation.animatedOnScroll) {
          const salaryBounds = salary.getBoundingClientRect();
          if (salaryBounds.top < window.innerHeight * 0.7) {
            this.switchCourseGroup(0, true);
            this.salaryAnimation.animatedOnScroll = true;
          }
        }

        const video = this.$el.querySelector('.video-container');
        if (video) {
          const videoBounds = video.getBoundingClientRect();
          if (videoBounds.top < window.innerHeight * 0.7 && videoBounds.top > window.innerHeight * -1 && this.$store.state.layout !== 'mobile' && !this.videoAlreadyPlayed) {
            clearTimeout(this.videoTimeout);
            this.videoTimeout = setTimeout(() => {
              this.$root.$emit('video-play', this.videoIds[this.$config.platform]);
              this.videoAlreadyPlayed = true;
            }, 200);
          }
        }

        if (this.$store.state.layout !== 'mobile' && !this.bannerClosed) {
          this.$nextTick(() => this.toggleBanner());
        }
      }
    },
    icon(icon) {
      const name = icon === 'sql' ? icon : `tech-responsive-${icon}`;
      return icon ? require(`@itbpbg/lms-components/src/assets/svg/${name}.svg?vue-component`) : '';
    },
    switchCourseGroup(idx, force = false) {
      if (idx === this.courseGroupIdx && !force) {
        return;
      }

      if (this.salaryAnimation.activate) {
        this.salaryAnimation.activate = false;
      }

      let startAnimationAfter = 10;

      if (!force && this.$store.state.layout === 'mobile') {
        const coursesSlider = this.$el.querySelector('.courses-extended .infinite-carousel');
        if (coursesSlider) {
          startAnimationAfter = 500;
          coursesSlider.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }

      this.salaryAnimation.animatedOnScroll = true;

      setTimeout(() => {
        this.courseGroupIdx = idx;
        this.$nextTick(() => {
          let width = 0;
          const holder = this.$el.querySelector('.animation-salary .holder');
          const letters = holder.querySelectorAll('.letter');
          for (let idx2 = 0; idx2 < letters.length; idx2 += 1) {
            const letter = letters[idx2];
            width += letter.offsetWidth;
          }
          this.salaryAnimation.width = width;
          this.salaryAnimation.activate = true;
          setTimeout(() => {
            for (let idx3 = 0; idx3 < letters.length; idx3 += 1) {
              const letter = letters[idx3];
              setTimeout(() => {
                letter.classList.add('show');
              }, idx3 * 100);
            }
          }, 200);
        });
      }, startAnimationAfter);
    },
    subscribe() {
      const plan = this.$store.getters['subscriptions/annual'] || {};
      this.$store.dispatch('dataLayer/addEvent', { event: 'addToCart', value: { ...plan, planTitle: plan.title, currency: plan.currency.value } });

      return this.$router.push({
        path: '/checkout/',
        query: { plan: 'lifetime', coupon: this.promotion.coupon },
      });
    },
    toggleBanner() {
      const bottomBanner = document.querySelector('.bottom-banner');
      const hero = document.querySelector('.courses-extended');
      const footer = document.querySelector('.limited-offer-holder');
      let hideFromTop = false;
      let hideFromBottom = false;

      if (bottomBanner) {
        if (hero) {
          hideFromTop = hero.getBoundingClientRect().top - 100 > 0;
        }

        if (footer) {
          hideFromBottom = footer.getBoundingClientRect().top - 400 < 0;
        }

        if (hideFromTop || hideFromBottom) {
          bottomBanner.classList.remove('fixed');
        } else {
          bottomBanner.classList.add('fixed');
        }
      }
    },
    closeBanner() {
      this.bannerClosed = true;
    },
    saleEndDate() {
      const campaignData = CAMPAIGN_PROMOTIONS['cyber-monday-lifetime-2024'];
      const promoOptions = getPromotion(campaignData.promos, this.promotion);
      this.promotion = {
        buttonText: 'Start Now',
        ...promoOptions.nextPromotion,
        ...campaignData.templates,
      };
      if (promoOptions.refresh) {
        setTimeout(() => {
          window.$nuxt.refresh();
        }, 40000);
      }

      return new Date(this.promotion.endDate);
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes heroAnimation {
  0% {
    transform: rotateX(0deg);
  }

  50% {
    transform: rotateX(90deg);
  }

  100% {
    transform: rotateX(0deg);
  }
}

@include bp($bp-desktop) {
  .mobile-only,
  .laptop-only,
  ::v-deep .mobile-only,
  ::v-deep .laptop-only {
    display: none !important;
  }
}

.block,
::v-deep .block {
  display: block;
}

.highlight,
::v-deep .highlight {
  font-weight: 700;

  &.campaign-1,
  &.blue {
    color: var(--c-cml2024-1);
  }

  &.campaign-2,
  &.yellow {
    color: var(--c-cml2024-2);
  }

  &.campaign-3,
  &.magenta {
    color: var(--c-cml2024-3);
  }

  &.white {
    font-weight: 600;
    color: var(--main-light);
  }

  &.thin {
    font-weight: inherit;
  }
}

.fa {
  .highlight,
  ::v-deep .highlight {
    &.fa-campaign-1,
    &.fa-blue {
      color: var(--c-cml2024-1);
    }

    &.fa-campaign-2,
    &.fa-yellow {
      color: var(--c-cml2024-2);
    }

    &.fa-campaign-3,
    &.fa-magenta {
      color: var(--c-cml2024-3);
    }
  }
}

::v-deep {
  .course-card.dark {
    .course-card-body {
      .course-info {
        .course-name {
          font-size: 18px;
          font-weight: 500;
        }

        .info {
          font-size: 16px;
          font-weight: 400;
        }
      }
    }

    .course-card-container {
      background-color: var(--main-dark-light-1);
    }
  }
}

.extend-label {
  margin-top: 30px;
  display: inline-block;
  font-size: 24px;
  font-weight: 700;
  padding: 12px;
  line-height: 18px;
  background-color: var(--c-lda-3);
  transform: rotate(-5deg);
}

.btn-primary,
::v-deep .btn-primary {
  color: var(--main-dark);
  border: 2px solid var(--c-lda-1);
  background-color: var(--c-lda-1);
  font-weight: 600;
  font-size: 21px;
  border-radius: 0;
  margin: 0 auto;
  display: block;
  height: 58px;
  line-height: 18px;
  max-width: 350px;
  width: 100%;
  margin-top: var(--spacing-60);
  padding-left: 5px;
  padding-right: 5px;

  &.submit {
    margin-top: 20px;
    margin-bottom: 12px;
    line-height: 30px;
    outline: 2px solid var(--main-dark) !important;
  }

  &:not(:disabled) {
    &:hover,
    &:active,
    &:focus {
      color: var(--main-dark);
      background-color: var(--c-lda-1);
      border-color: var(--c-lda-1);

      &:not(.submit) {
        box-shadow: 0px 0px 15px #FAFF00;
      }
    }
  }
}

::v-deep .limited-offer .btn-primary.submit,
::v-deep .bottom-banner .btn-primary.submit {
  background-color: var(--c-mw-2);

  &:not(:disabled) {
    &:hover,
    &:active,
    &:focus {
      color: var(--main-dark);
      background-color: var(--c-mw-2);
    }
  }
}

.landing-page-v2 {
  background-color: var(--main-dark);
  color: var(--main-light);
  min-height: 100vh;

  h1 {
    font-size: 64px;
    font-weight: 500;
    line-height: 1.1;
    margin-bottom: 40px;

    .code-block img {
      height: 35px;
      display: block;
      margin-bottom: var(--spacing-8);
    }
  }

  .section-title,
  ::v-deep .section-title {
    font-size: 40px;

    // .highlight {
    //   font-weight: inherit;
    // }

    .smaller {
      display: block;
      margin-top: 12px;
      font-size: 32px;
    }
  }

  .muted {
    color: var(--main-light-shade-4);
  }

  .subtitle {
    font-size: 18px;
    font-weight: 500;
  }

  .animation {
    font-family: "Source Code Pro", monospace;
    font-optical-sizing: auto;
    font-weight: 600;
    font-style: normal;
    display: inline-block;
    font-size: 18px;
    line-height: 18px;
    width: 180px;
    height: 23px;
    color: var(--main-dark);
    background-color: #F0F0F2;
    border: 1px solid #ABAFB5;
    text-align: center;
    transform-style: preserve-3d;

    &.animate {
      animation: heroAnimation 0.6s ease-in-out;
    }
  }

  .static-salary-container {
    width: 188px;
    display: inline-block;
  }

  .animation-salary {
    vertical-align: middle;
    display: inline-block;
    width: 188px;

    .holder {
      display: block;
      white-space: nowrap;
      overflow: hidden;
      width: 0;

      &.animation {
        text-align: left;
        line-height: 23px;
      }

      &.active {
        transition: width 0.5s;
        width: var(--width, 188px);
      }
    }

    ::v-deep {
      .letter {
        font-weight: normal;
        opacity: 0;
        transition: all 0.1s linear;

        &.show {
          opacity: 1;
        }

        &:first-child {
          padding-left: 4px;
        }

        &:last-child {
          padding-right: 4px;
        }

        &.bold {
          font-weight: 700;
        }
      }
    }
  }

  .section {
    background-size: cover;
    background-position: bottom;
    padding-top: 80px;
    padding-bottom: 80px;

    &.instructors {
      .section-title {
        text-align: center;
      }

      ::v-deep .infinite-head .h4 {
        text-align: center;
      }
    }
  }

  &.extend {
    .highlight.yellow.special {
      text-shadow: 4px 4px 0 var(--main-dark);
    }

    .btn-primary,
    ::v-deep .btn-primary {
      &.submit:not(:hover) {
        box-shadow: 6px 6px 0 0 var(--c-lda-3);
      }
    }

    ::v-deep .bottom-banner {
      .campaign-heading {
        .extend-label {
          transform: none;
          padding-top: 8px;
          padding-bottom: 8px;
          font-size: 16px;
          margin: 0;
        }

        .block {
          display: inline;
        }

        .highlight.blue {
          font-size: 52px;
        }
      }
    }
  }

  &.ds .hero {
    background-image: url('../assets/images/campaigns/ds-cml2024-bg-hero.webp');
  }

  &.fa {
    .hero {
      background-image: url('../assets/images/campaigns/fa-cml2024-bg-hero.webp');

      ::v-deep {
        .holder .card {
          border-color: var(--accent);
          box-shadow: 0 0 90.2px 0 var(--accent);

          .counter.filled .square {
            border-color: var(--c-cml2024-1);
          }
        }

        .hero-benefits {
          .benefit svg [stroke] {
            stroke: var(--c-cml2024-1);
          }
        }
      }
    }

    ::v-deep .limited-offer-holder .limited-offer .switch-holder .switch input:checked + .slider {
      background-color: #68A9FE;
    }
  }

  .hero {
    min-height: 700px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-size: auto 100%;
    background-position: center;
    background-repeat: no-repeat;
    padding-top: 0;

    ::v-deep .trusted-companies {
      margin-top: var(--spacing-36);
      padding-top: var(--spacing-36);
      border-color: rgba(255, 255, 255, 0.24);

      .logos {
        flex-wrap: wrap;
        justify-content: center;
        row-gap: 32px;
        column-gap: 66px;

        .logo {
          svg {
            height: 24px;
            color: var(--main-light-shade-3);

            g[fill] {
              fill: var(--main-light-shade-3);
            }

            &.pwc {
              height: 28px;
            }
          }
        }
      }
    }

    ::v-deep .content {
      padding-right: 0;

      .title .main {
        font-size: 80px;
        margin-bottom: 12px;
        line-height: 0.9;

        .highlight {
          font-weight: 900;
          white-space: nowrap;

          &.blue {
            letter-spacing: 1.28px;
          }
        }

        svg {
          height: 141px;
        }
      }

      .text-2 {
        font-size: 24px;
        line-height: 1.1;
      }
    }

    ::v-deep .hero-benefits {
      font-size: 16px;
      line-height: 1.4;

      .benefit svg [stroke] {
        stroke: var(--c-cml2024-3);
      }
    }

    ::v-deep .holder {
      display: flex;
      align-items: center;

      .card {
        padding: var(--spacing-36);
        background-color: var(--main-light);
        border: 5px solid var(--c-cml2024-3);
        box-shadow: 0px 0px 90.2px 0px rgba(255, 66, 240, 0.8);
        position: relative;
        color: var(--main-dark);

        .label {
          color: var(--main-dark-light-4);
          font-size: 20px;
          font-weight: 600;
          margin: 0;
        }

        .old-price {
          font-size: 30px;
          line-height: 110%;
          font-weight: 500;
          margin-top: var(--spacing-12);
          margin-bottom: 0;
          position: relative;
          display: inline-block;

          .del:after {
            content: '';
            width: 92%;
            height: 2px;
            background: var(--main-dark-light-1);
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
        }

        .new-price {
          font-size: 72px;
          font-weight: 500;
          line-height: 1.1;
          margin-top: var(--spacing-4);
          margin-bottom: var(--spacing-4);
          display: flex;
          align-items: flex-end;
          justify-content: flex-start;

          .medium {
            font-size: 30px;
            line-height: 50px;
          }

          .small {
            font-size: 21px;
            font-weight: 400;
            line-height: 50px;
          }
        }

        .text {
          margin: 12px 0;
          font-size: 21px;
          line-height: 1.4;
        }

        .counter {
          &.filled .square {
            width: 22.5%;
            border-radius: 0;
            background-color: var(--main-dark-light-2);
            border: 1px solid var(--c-cml2024-3);
          }
        }

        .card-text {
          font-size: 14px;
          line-height: 150%;
          color: var(--main-dark-light-2);
          text-align: center;
          margin: 0;
        }

        .stamp {
          position: absolute;
          top: 36px;
          right: 36px;

          img {
            display: block;
            width: 80px;
          }
        }
      }
    }

    .footer-text {
      strong {
        font-size: 22px;
      }
    }
  }

  .technologies {
    padding-top: 40px;

    .technologies-title {
      text-align: center;
      font-size: 18px;
      font-weight: 500;
      margin-bottom: 28px;
    }

    .container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 40px;
      border-bottom: 1px solid var(--main-dark-light-4);

      .single-technology {
        display: flex;
        align-items: center;
        color: var(--main-light-shade-3);

        svg {
          height: 24px;
          margin-right: 10px;

          // [fill] {
          //   fill: var(--main-light-shade-3);
          // }

          // [stroke] {
          //   stroke: var(--main-light-shade-3);
          // }
        }
      }
    }
  }

  &.fa .technologies .container {
    border-bottom: 0;

    .single-technology svg {
      height: 32px;
    }
  }

  .instructors {
    ::v-deep {
      .trusted-companies {
        padding-top: 0;
        border-top: none;
        color: var(--main-light);

        .logos {
          justify-content: center;
          column-gap: 70px;
        }
      }

      .infinite-carousel .carousel .inner > * {
        width: 210px;
        margin-right: 30px;
        border: 4px solid var(--main-dark);
        border-radius: 0;

        img {
          display: block;
          width: 100%;
        }
      }
    }
  }

  .section-footer {
    border-top: 1px solid var(--main-dark-light-3);
    margin-top: 30px;
    padding-top: 32px;

    > * + * {
      margin-top: 40px;
    }

    ::v-deep .trusted-companies {
      margin-top: 0;
      padding-top: 0;
      border-top: none;

      .title {
        color: var(--main-light);
      }

      .logos {
        .logo {
          svg {
            height: 30px;

            &,
            g[fill] {
              color: var(--main-light-shade-4);
            }

            path[fill],
            g[fill] {
              fill: var(--main-light-shade-4);
            }
          }
        }
      }
    }

    .trustpilot-review {
      display: flex;
      align-items: flex-end;
      justify-content: center;

      svg {
        height: 40px;
        margin-right: 26px;

        ~ svg {
          height: 38px;
          margin-right: 0;
        }
      }

      .rating {
        font-size: 28px;
        font-weight: 500;
        color: var(--main-light-shade-4);
        margin-right: 20px;
      }
    }
  }

  .btn-holder {
    display: flex;
    justify-content: center;
    margin-top: 32px;
  }

  .stats {
    background-color: var(--main-dark-light-1);

    ::v-deep .stats-icons {
      margin-top: 0;

      > div {
        align-items: center;

        .description {
          font-size: 21px;
        }
      }

      svg {
        width: 90px;
        height: 90px;
      }
    }
  }

  .courses-extended {
    .head {
      text-align: center;

      h3 {
        margin: 40px 0;
      }

      ul {
        list-style: none;
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 16px;
        padding-left: 0;

        li {
          padding: 20px;
          font-size: 18px;
          line-height: 1.1;
          color: var(--main-light);
          background-color: var(--main-dark-light-1);
          border: 2px solid transparent;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover,
          &.active {
            border-color: var(--c-cml2024-3);
            background-color: var(--main-dark-light-2);
          }
        }
      }

      .description {
        font-size: 24px;
        font-weight: 500;
        margin-top: 60px;
        margin-bottom: 0;
      }
    }

    ::v-deep .courses {
      padding-bottom: 0;
    }

    .btn-holder {
      margin-top: 0;
    }
  }

  .promo-video {
    .video {
      padding-top: 0;
      padding-bottom: 0;

      ::v-deep {
        h2 {
          max-width: 100%;
        }

        .video-js {
          border-radius: 0;
          border: none;
          box-shadow: none;
        }
      }
    }

    ::v-deep .stats-icons .description {
      white-space: nowrap;
    }
  }

  .free {
    .section-title {
      text-align: center
    }

    .section-description {
      text-align: center;
      font-size: 21px;
      font-weight: 400;
      line-height: 1.4;
      margin-bottom: 40px;
    }

    .cards {
      row-gap: 30px;

      .card {
        .card-image {
          display: block;
          max-width: 100%;
          margin: auto;
        }

        .card-title {
          margin-top: 20px;
        }

        .card-desc {
          font-size: 18px;
          line-height: 1.4;
        }
      }
    }

    .btn-holder {
      margin-top: 0;
    }
  }

  .courses-extended {
    ::v-deep .infinite-carousel {
      .controls {
        margin-top: 0;
      }

      .carousel {
        padding-top: 10px;
        margin-top: -10px;
        padding-bottom: 10px;
        margin-bottom: -10px;

        .card {
          margin: 0 var(--spacing-30) 0 0;
          width: 370px;
          vertical-align: top;

          .course-card-container {
            min-height: 422px;
          }

          &:not(.course-card) {
            flex-direction: column;
            align-items: flex-start;
            height: auto;
            border-radius: 0;

            .image-wrapper img {
              display: block;
              max-width: 100%;
              max-height: 100%;
              border-radius: 7px;
            }

            .card-title {
              margin-top: var(--spacing-20);
              font-size: 32px;
              color: var(--main-light);
            }

            .card-desc {
              color: var(--main-dark-shade-4);
              font-size: 21px;
              line-height: 140%;
            }
          }

          &.course-card {
            .course-card-image {
              height: 220px;
            }

            .course-card-body {
              padding: var(--spacing-20) var(--spacing-24) 20px;

              .course-name {
                margin-bottom: var(--spacing-8);
              }

              .course-rating {
                color: var(--main-light);
              }

              .info {
                color: var(--main-light);
                margin: 0;

                .dot {
                  background: var(--main-light);
                }
              }
            }
          }
        }
      }
    }
  }

  .master {
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), #000C1F;

    .muted {
      font-size: 21px;
    }

    img {
      display: block;
      max-width: 100%;
    }
  }

  .graduations {
    background-size: cover;
    background-position: bottom;

    ::v-deep {
      .title-highlight {
        font-size: 32px;

        svg {
          height: 38px;
        }
      }

      .graduates-thoughts .videos {
        .heading {
          margin-bottom: 0;
        }

        .subtitle {
          margin-top: 12px;
        }

        .controls {
          margin-top: 34px;
        }

        .single-video {
          .video-image {
            border-radius: 0;
          }

          .quote svg {
            margin-bottom: 4px;

            [fill] {
              fill: var(--c-cml2024-1);
            }
          }
        }
      }
   }

    .heading img {
      height: 44px;
      display: inline-block;
      vertical-align: bottom;
    }
  }

  .dream {
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), #000C1F;
    text-align: center;

    .title-description {
      font-size: 32px;
      font-weight: 500;
      margin: 0;
    }

    .row {
      margin-top: 60px;
      margin-bottom: 18px;
    }

    .col {
      svg {
        color: var(--accent);
      }

      .title {
        font-size: 28px;
        font-weight: 500;
        color: var(--accent);
      }

      .description {
        font-size: 18px;
        font-weight: 400;
        line-height: 1.5;
      }
    }
  }

  .projects {
    ::v-deep {
      .home-small .card,
      .home-big .card {
        border-radius: 0;
      }

      .subtitle {
        margin-left: 0;
        width: 100%;
        max-width: 100%;
        flex: 1
      }
    }
  }

  .long-reviews-holder {
    ::v-deep {
      .title {
        text-align: center;
        margin-right: 0;
      }

      .reviews-trustpilot {
        .reviews-body .card-trustpilot-review {
          border-radius: 0;
        }

        .dark .see-more:after {
          bottom: 0;
          background: linear-gradient(2deg, #011633 2.01%, rgba(1, 22, 51, 0.77) 35.57%, rgba(1, 22, 51, 0.00) 86.79%);
        }
      }
    }
  }

  &.ds ::v-deep .limited-offer-holder {
    background-image: url('../assets/images/campaigns/ds-cml2024-bg-footer.webp');
  }

  &.fa ::v-deep .limited-offer-holder {
    background-image: url('../assets/images/campaigns/fa-cml2024-bg-footer.webp');
  }

  ::v-deep .limited-offer-holder {
    position: relative;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center top;

    .limited-offer {
      position: relative;
      z-index: 2;
      color: var(--main-light);

      h2 {
        margin-bottom: var(--spacing-40);

        .highlight {
          font-weight: 900;
        }
      }

      .subtitle {
        color: var(--main-light);
        font-size: 32px;
        line-height: 1.1;
        margin-bottom: 12px;
      }

      .heading {
        font-size: 32px;

        .highlight.block {
          font-size: 40px;
          margin-bottom: 12px;
        }
      }

      .price {
        margin-top: 0;
        margin-bottom: 40px;
        padding-bottom: 0;

        .regular-price {
          align-self: flex-start;
          margin-top: 10px;
          font-weight: 500;
          color: #E6E7E9;

          &:before {
            background-color: #E6E7E9;
          }
        }

        .discounted-price {
          line-height: 70px;
          font-weight: 700;
          font-size: 60px;

          .body-1 {
            font-weight: 400;
          }
        }
      }

      .plan-features {
        margin-bottom: var(--spacing-50);

        .feature {
          position: relative;
          line-height: 150%;
          margin-bottom: 8px;

          svg {
            [fill] {
              fill: var(--c-cml2024-1);
            }

            [stroke] {
              stroke: var(--c-cml2024-1);
            }
          }
        }
      }

      hr {
        display: none;
      }

      .switch-holder {
        span:not(.unactive) {
          color: var(--main-light);
        }

        .switch {
          width: 45.69px;
          height: 22px;

          .slider:before {
            width: 16px;
            height: 16px;
            top: 3px;
            left: 3px;
          }

          // input:not(:checked) + .slider {
          //   background-color: var(--c-tyf-1);
          // }

          input:checked + .slider {
            background-color: var(--c-cml2024-3);

            &:before {
              transform: translateX(23px);
            }
          }
        }
      }

      .btn-primary {
        margin-top: 0;
      }

      .badges {
        margin-top: 40px;
      }

      .vat-note {
        margin-top: var(--spacing-24);
        color: var(--main-light);
        font-weight: 400;
        font-size: 18px;
      }
    }

    .limited-offer-background {
      position: absolute;
      top: 0;
      left: 50%;
      bottom: 0;
      width: 100%;
      height: calc(100% + 50px);
      object-fit: cover;
      transform: translateX(-50%);
    }
  }

  ::v-deep .bottom-banner {
    background-image: url('../assets/images/campaigns/ds-cml2024-bg-bottom-banner.webp');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    height: 110px;
    padding: 0;
    border-top: 3px solid var(--extreme-yellow);

    &.final-promo {
      background-image: url('../assets/images/campaigns/ds-cml2024-bg-footer.webp');
    }

    .container {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }

    .campaign-heading {
      display: flex;
      align-items: center;

      .highlight {
        &.white {
          font-size: 48px;
          font-weight: 800;
          line-height: 0.9;
        }
      }

      .block {
        display: block;
        font-size: 20px;
        font-weight: 700;
        text-align: right;
      }

      svg {
        margin-left: 12px;
        height: 65px;
      }
    }

    .counter.filled .square {
      border-radius: 0;
      border: 2px solid var(--c-cml2024-2);
    }

    .btn-primary {
      width: 220px;
      height: 58px;
      margin-top: 0;
      margin-left: 0;
      margin-right: 0;
      line-height: 20px;
      border: 0;
    }

    .icon-close {
      top: 19px;
      right: 14px;

      path[fill] {
        fill: var(--main-light-shade-2);
      }
    }
  }

  &.fa ::v-deep .bottom-banner {
    border-color: var(--c-cml2024-3);
  }

  @include bp($bp-laptop) {
    .section-title,
    ::v-deep .section-title {
      font-size: 32px;

      .smaller {
        font-size: 28px;
      }
    }

    .extend-label {
      margin-top: 10px;
      font-size: 21px;
      line-height: 12px;
      margin-bottom: 10px;
    }

    &.ds .hero {
      background-image: url('../assets/images/campaigns/ds-cml2024-bg-hero-laptop.webp');
    }

    &.fa .hero {
      background-image: url('../assets/images/campaigns/fa-cml2024-bg-hero-laptop.webp');
    }

    .hero {
      .content {
        .title .main {
          .highlight {
            &.blue {
              font-size: 48px;
            }

            &.yellow {
              font-size: 93px;
            }
          }

          svg {
            height: 126px;
          }
        }

        .text-2 {
          font-size: 21px;
        }
      }

      ::v-deep .holder .card .new-price {
        font-size: 60px;
      }
    }

    .instructors {
      ::v-deep {
        .infinite-carousel .carousel .inner > * {
          width: 178px;
        }
      }
    }

    .section-footer ::v-deep {
      .trusted-companies {
        .logos {
          .logo svg {
            height: 22px;
          }
        }
      }
    }

    .courses-extended {
      .head {
        ul li {
          font-size: 14px;
        }

        .description {
          font-size: 21px;
        }
      }

      ::v-deep {
        .infinite-carousel .carousel .course-card {
          width: 310px;
        }
      }
    }

    .stats ::v-deep .stats-icons {
      svg {
        width: 60px;
        height: 60px;
      }

      > div .description {
        font-size: 18px;
      }
    }

    .dream {
      .title-description {
        font-size: 28px;
      }

      .col {
        .title {
          font-size: 24px;
        }
      }
    }

    .landing-footer {
      .title-description {
        font-size: 28px;
      }

      .title-description-2 {
        font-size: 32px;
      }
    }

    ::v-deep .limited-offer-holder {
      .limited-offer {
        .limited-offer-container {
          width: 700px;
          max-width: 700px;
        }

        .subtitle {
          font-size: 28px;
        }

        .heading {
          &,
          .text-2,
          .text-3 {
            font-size: 32px;
          }

          img {
            height: 60px;
          }
        }

        .subtitle .title-inline {
          width: 240px;
        }

        .price {
          .discounted-price {
            font-size: 54px;
          }
        }

        .btn-primary {
          max-width: 310px;
        }
      }

      .limited-offer-background {
        bottom: -160px;
      }
    }

    ::v-deep .bottom-banner {
      .campaign-heading {
        .highlight {
          &.white {
            font-size: 40px;
            font-weight: 800;
            line-height: 0.9;
          }
        }
      }

      .btn-primary {
        width: 180px;
        font-size: 18px;
      }
    }

    &.extend {
      ::v-deep .bottom-banner {
        .campaign-heading {
          .extend-label {
            font-size: 14px;
          }

          .highlight {
            font-size: 36px;

            &.blue {
              font-size: 48px;

              .smaller {
                font-size: 32px;
              }
            }
          }
        }

        .counter {
          .square:not(:last-of-type) {
            margin-right: 8px;
          }
        }
      }
    }
  }

  @include bp($bp-mobile) {
    .desktop-only,
    ::v-deep .desktop-only {
      display: none !important;
    }

    .static-salary-container,
    .animation-salary {
      width: auto;
    }

    .extend-label {
      margin: 0 auto 20px;
      font-size: 21px;
      line-height: 12px;
    }

    .btn-holder {
      margin-top: 0;
    }

    .btn-primary.submit,
    ::v-deep .btn-primary.submit {
      font-size: 18px;
      line-height: 14px;
    }

    .section {
      padding-top: 60px;
      padding-bottom: 60px;
      background-size: contain;
      background-position: bottom;
      background-repeat: no-repeat;
    }

    :not(.hero) .btn-primary {
      width: 100%;
      max-width: 327px;
    }

    .subtitle {
      font-size: 20px;
    }

    .section-title,
    ::v-deep .section-title {
      font-size: 32px;

      .smaller {
        font-size: 28px;
      }
    }

    .hero {
      padding-top: 108px;
      padding-bottom: 60px;
      background-size: cover;
      background-position: top center;

      ::v-deep .content {
        .title {
          max-width: 422px;
          margin-left: auto;
          margin-right: auto;

          .main {
            text-align: center;

            .highlight {
              font-size: 76px;

              &.white {
                display: block;
              }
            }

            .block {
              display: block;
              font-size: 28px;
            }

            svg {
              height: 134px;
            }
          }

          .text-2 {
            font-size: 21px;
          }
        }
      }

      ::v-deep .trusted-companies {
        margin-top: 40px;
        padding-top: 40px;
        margin-bottom: 60px;

        .logos {
          column-gap: 40px;

          .logo svg {
            height: 20px;
          }
        }
      }

      ::v-deep .holder {
        padding: 0;
        margin-top: 0;
        flex-direction: column;

        .card {
          padding: 24px;
          margin-left: auto;
          margin-right: auto;
          margin-bottom: 0;

          .stamp {
            top: 20px;
            right: 24px;
            width: 70px;
            height: 70px;

            img {
              width: 70px;
            }
          }

          .card-text {
            font-size: 12px;
          }

          .old-price {
            font-size: 24px;
          }

          .discounted-price {
            font-size: 59px;
          }

          .text {
            font-size: 18px;
          }
        }

        .hero-benefits {
          max-width: 422px;

          .benefit {
            font-size: 16px;
          }
        }

        .footer-text {
          display: block;
          text-align: center;
          margin-top: 40px;
        }
      }

      .mobile-only {
        margin-top: 40px;

        .subtitle {
          font-weight: 400;
        }
      }
    }

    .instructors ::v-deep .infinite-carousel .carousel .inner > * {
      width: 327px;
    }

    > .technologies .container {
      width: auto;
      padding: 0 0 36px;
      margin: 0 24px;
      flex-wrap: wrap;
      column-gap: 25px;
      row-gap: 50px;
      justify-content: space-evenly;
    }

    &.fa .technologies .container {
      border-bottom: 1px solid var(--main-dark-light-4);

      .single-technology svg {
        height: 27px;
      }
    }

    .section-footer {
      padding-left: 0;
      padding-right: 0;
      margin-left: 24px;
      margin-right: 24px;
      width: auto;

      ::v-deep {
        .trusted-companies {
          margin-bottom: 30px;

          .title {
            margin-top: 0;
            margin-bottom: 30px;
          }

          .logos {
            .logo svg {
              height: 28px;
              margin-right: 0;
            }
          }
        }

        .trustpilot-review {
          margin-top: 0;
          justify-content: space-between;
          max-width: 375px;

          > svg {
            height: 26px;
            margin-right: auto;
            flex: 0 0 auto;

            ~ svg {
              height: 26px;
            }
          }

          .rating {
            font-size: 20px;
            margin-right: auto;
          }
        }
      }

      .btn-primary {
        margin-top: 32px;
      }
    }

    ::v-deep .trusted-companies .logos .logo svg {
      height: 23px;
    }

    ::v-deep .hero-benefits .benefit {
      font-size: 18px;
    }

    .courses-extended {
      .head {
        ul {
          gap: 12px;
          justify-content: flex-start;
        }

        .description {
          margin-top: 32px;

          .highlight {
            display: block;
          }
        }
      }

      .courses {
        padding-top: 32px;
        padding-bottom: 0;
      }

      .btn-holder {
        margin-top: 0;
      }

      ::v-deep .infinite-carousel .carousel .card {
        width: 327px;
      }
    }

    .stats ::v-deep .stats-icons {
      svg {
        width: 50px;
        height: 50px;
      }
      > div .description {
        font-size: 18px;
        white-space: nowrap;
      }
    }

    .master {
      text-align: center;
    }

    .graduations {
      ::v-deep {
        .col {
          padding-left: 26px;
          padding-right: 26px;
        }
      }
    }

    .dream {
      h2 {
        font-size: 32px;
      }

      .title-description {
        font-size: 28px;
      }
    }

    &.ds {
      .hero {
        background-image: url('../assets/images/campaigns/ds-cml2024-bg-hero-mobile.webp');
      }

      ::v-deep .limited-offer-holder {
        background-image: url('../assets/images/campaigns/ds-cml2024-bg-footer-mobile.webp');
      }
    }

    &.fa {
      .hero {
        background-image: url('../assets/images/campaigns/fa-cml2024-bg-hero-mobile.webp');
      }

      ::v-deep .limited-offer-holder {
        background-image: url('../assets/images/campaigns/fa-cml2024-bg-footer-mobile.webp');
      }
    }

    ::v-deep .limited-offer-holder {
      padding-top: 60px;
      padding-bottom: 60px;
      background-size: auto;

      .limited-offer {
        .plan-features {
          text-align: left;
          display: block;
          width: 100%;

          .feature {
            font-size: initial;
          }
        }

        .price {
          align-items: flex-start;

          .regular-price {
            font-size: 24px;
            margin-right: 8px;
          }

          .discounted-price {
            font-size: 50px;
          }
        }

        .subtitle {
          font-size: 28px;
          line-height: 110%;
          display: block;
          text-align: center;

          .title-inline {
            display: block;
            width: 258px;
            margin: 10px 0;
          }
        }

        .heading {
          font-size: 32px;

          .text-2 {
            font-size: 32px;
            margin: 12px 0;
          }

          .text-3 {
            font-size: 32px;
          }
        }
      }

      .limited-offer-background {
        top: 0;
        bottom: 0;
        width: 100%;
        object-fit: cover;
        object-position: top;
        height: 100%;
      }
    }
  }
}
</style>
